import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { GoalSubSectorModal } from '../components/GoalSubSectorModal';
import { goalSectorsApi, goalSubSectorsApi } from '../budgetGoalsApi';
import type { GoalSubSectorListItem } from '../budgetGoalTypes';

/**
 * The goal catalog's middle rung, reached through **Procurement → Investment Planning →
 * Settings**.
 *
 * Its SECTOR is a facet as well as a column, and the list files by it through the folder-nav
 * strip — which is the cut a reader takes most often here.
 */
export function GoalSubSectorsIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<GoalSubSectorListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    subSector: GoalSubSectorListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    A sub-sector cannot be created before a sector exists — the form's sector picker is
    REQUIRED, so the module is unusable until the rung above it holds a row. `<IndexPage>`
    says so before the reader opens the form rather than after they fill it.

    Seeded MET, and a failed feed leaves it met: flashing "set up sectors first" on every load
    of a working module is worse than a banner arriving a moment late, and a request that did
    not come back is not evidence that a catalog is empty.
  */
  const [hasSectors, setHasSectors] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    goalSectorsApi
      .options(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setHasSectors(rows.length > 0);
      })
      .catch(() => {
        // Leaves it met.
      });

    return () => controller.abort();
  }, [reloadKey]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchSubSectors = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      goalSubSectorsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          sector: query.facets.sector?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (subSector: GoalSubSectorListItem): MenuItem[] => [
      {
        key: `edit-${subSector.id}`,
        label: 'Edit',
        permission: 'budget-goals.update',
        onClick: () => setEditing(subSector),
      },
      {
        key: `restore-${subSector.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'budget-goals.delete',
        disabledReason: subSector.isActive ? 'This sub-sector is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', subSector }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await goalSubSectorsApi.deactivate(confirming.subSector.id);
      } else {
        await goalSubSectorsApi.activate(confirming.subSector.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<GoalSubSectorListItem>
        module="budget-goals"
        moduleNoun="goal sub-sectors"
        breadcrumb={[
          'Procurement',
          'Investment Planning',
          { label: 'Settings', to: '/procurement/investment-planning/settings' },
          'Sub-sectors',
        ]}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'sector', label: 'Sector' },
        ]}
        // Files by SECTOR rather than by status: that is the cut a reader takes here, and the
        // reader can repoint it from the rail's cog if they disagree.
        folderNavFacet="sector"
        auditEntityTypes={['GoalSubSector']}
        rowKey={(subSector) => subSector.id}
        fetch={fetchSubSectors}
        reloadKey={reloadKey}
        searchPlaceholder="Search sub-sectors or sectors"
        emptyMessage="No sub-sectors yet. Add the ones each sector is broken down into."
        prerequisites={[
          {
            label: 'a sector',
            met: hasSectors,
            to: '/procurement/investment-planning/settings/goal-sectors',
            permission: 'budget-goals.read',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="budget-goals.create"
        onRowOpen={(subSector) =>
          navigate(`/procurement/investment-planning/settings/goal-sub-sectors/${subSector.id}`)
        }
        openPermission="budget-goals.read"
        rowActions={rowActions}
        onDelete={(subSector) => setConfirming({ kind: 'deactivate', subSector })}
        deletePermission="budget-goals.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(subSector) =>
          subSector.isActive ? null : 'This sub-sector is already deleted.'
        }
        onClearData={() => goalSubSectorsApi.clear()}
        clearDataPermission="budget-goals.clear-data"
        otherInfo={(subSector) => subSector}
      />

      {creating || editing !== null ? (
        <GoalSubSectorModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete sub-sector' : 'Restore sub-sector'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`goal-sub-sector-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.subSector.name}" stops being offered when a goal is filed.`
                  : `"${confirming.subSector.name}" is offered again when a goal is filed.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.subSector.goalCount > 0 ? (
                <p>
                  The {confirming.subSector.goalCount} goal
                  {confirming.subSector.goalCount === 1 ? '' : 's'} under it are kept and stay in
                  the Goals list. What changes is that they stop being offered to a budget plan,
                  and nothing new can be filed here.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted sub-sector can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<GoalSubSectorListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 260,
    flex: true,
    sortable: true,
    render: (subSector) => <TextCell value={subSector.name} />,
  },
  {
    key: 'sectorName',
    header: 'Sector',
    width: 200,
    sortable: true,
    summary: true,
    /*
      NOT gated. It is another RUNG of this module rather than another module's field — the
      whole hierarchy sits behind `budget-goals.read` — so a gate here could never hide
      anything, which is the defect the personnel list's plantilla columns are named for.
    */
    render: (subSector) => <TextCell value={subSector.sectorName} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 300,
    summary: true,
    render: (subSector) => <TextCell value={subSector.description} />,
  },
  {
    key: 'goalCount',
    header: 'Goals',
    width: 100,
    align: 'right',
    sortable: true,
    render: (subSector) => <TextCell value={String(subSector.goalCount)} mono numeric />,
  },
  {
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    // The stored value, counted within its own sector. `mono` but not `numeric` — an order is
    // an identifier, not a quantity, so a zero must not dim.
    render: (subSector) => <TextCell value={String(subSector.sortOrder)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (subSector) =>
      subSector.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
